<template>
  <div
    class="flex flex-col sm:flex-row items-center sm:items-start gap-5 rounded-2xl border border-gray-100 bg-white shadow-sm p-6"
  >
    <div class="relative">
      <v-avatar color="primary" size="88">
        <ProtectedAvatar :src="avatarSrc" :size="88">
          <span class="text-2xl font-bold text-white">{{ initials }}</span>
        </ProtectedAvatar>
      </v-avatar>

      <button
        type="button"
        data-testid="profile-avatar-upload-button"
        :disabled="uploading"
        class="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-md ring-2 ring-white transition hover:bg-primary30 disabled:opacity-60"
        :aria-label="uploading ? 'Enviando foto' : 'Alterar foto de perfil'"
        @click="triggerFileInput"
      >
        <v-progress-circular v-if="uploading" indeterminate size="16" width="2" color="white" />
        <v-icon v-else size="18">{{ mdiCameraOutline }}</v-icon>
      </button>

      <input
        ref="fileInput"
        type="file"
        class="hidden"
        :accept="ACCEPTED_TYPES.join(',')"
        data-testid="profile-avatar-input"
        @change="onFileChange"
      />

      <AvatarCropModal
        :modal-active="isCropModalOpen"
        :file="selectedFile"
        @close="onCropModalClose"
        @cropped="onCropped"
      />
    </div>

    <div class="flex flex-col items-center sm:items-start">
      <h1 class="text-2xl font-bold text-gray-900 text-center sm:text-left">{{ name }}</h1>

      <p v-if="email" class="text-sm text-gray-500 mb-3">{{ email }}</p>

      <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2">
        <LabelBadge
          :label="statusConfig.label"
          :color="statusConfig.color"
          :icon="null"
          size="small"
        />
        <LabelBadge
          v-if="confirmed"
          label="E-mail verificado"
          color="green"
          :icon="null"
          size="small"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useToast } from 'vue-toastification'
import { mdiCameraOutline } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import AvatarCropModal from '@/components/designSystem/modals/AvatarCropModal/Index.vue'
import { getInitials, resolveAvatarUrl } from '../../helpers/profile'

// Espelha a união de cores de LabelBadge.vue (não importável como type de um .vue).
type LabelBadgeColor = 'blue' | 'orange' | 'green' | 'yellow' | 'red' | 'gray'

const props = defineProps<{
  name: string
  email?: string | null
  avatarUrl?: string | null
  isActive?: boolean
  confirmed?: boolean
  uploading?: boolean
}>()

const emit = defineEmits<{
  uploadAvatar: [file: File]
}>()

const MAX_FILE_SIZE = 10 * 1024 * 1024
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

const toast = useToast()

const fileInput = ref<HTMLInputElement | null>(null)
const selectedFile = ref<File | null>(null)
const isCropModalOpen = ref(false)

const avatarSrc = computed(() => resolveAvatarUrl(props.avatarUrl))
const initials = computed(() => getInitials(props.name))

const statusConfig = computed<{ label: string; color: LabelBadgeColor }>(() =>
  props.isActive ? { label: 'Ativo', color: 'green' } : { label: 'Inativo', color: 'gray' }
)

const triggerFileInput = () => fileInput.value?.click()

const onFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (!target.files || !target.files.length) return
  const file = target.files[0]
  // Reset antes da validacao para permitir re-selecionar o mesmo arquivo
  target.value = ''

  if (!ACCEPTED_TYPES.includes(file.type)) {
    toast.error('Formato inválido. Envie uma imagem JPEG, PNG ou WebP.')
    return
  }
  if (file.size > MAX_FILE_SIZE) {
    toast.error('A imagem excede o limite de 10MB.')
    return
  }

  selectedFile.value = file
  isCropModalOpen.value = true
}

const onCropModalClose = () => {
  isCropModalOpen.value = false
  selectedFile.value = null
}

const onCropped = (file: File) => {
  emit('uploadAvatar', file)
  onCropModalClose()
}
</script>
